import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AiInput } from '@/components/ui/ai-input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Link2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import type { ArticleSource } from '@/types/articles';

interface ArticleSourcesDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: ArticleSource[];
  onUpdateSources: (sources: ArticleSource[]) => void;
  onSyncLinksFromArticle: () => void;
  brandId: string | null;
  articleTitle: string;
}

export const ArticleSourcesDrawer: React.FC<ArticleSourcesDrawerProps> = ({
  open,
  onOpenChange,
  sources,
  onUpdateSources,
  onSyncLinksFromArticle,
  brandId,
  articleTitle,
}) => {
  const handleAddSource = () => {
    onUpdateSources([...sources, { title: '', description: '', link: '' }]);
  };

  const handleRemoveSource = (index: number) => {
    onUpdateSources(sources.filter((_, i) => i !== index));
  };

  const handleUpdateSource = (index: number, field: keyof ArticleSource, value: string) => {
    const newSources = [...sources];
    newSources[index] = { ...newSources[index], [field]: value };
    onUpdateSources(newSources);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent side="right" className="w-[480px] sm:max-w-[480px] flex flex-col p-0 mr-12" style={{ zIndex: 60 }}>
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <SheetTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5 text-primary" />
            Sources
            {sources.length > 0 && (
              <span className="ml-1 bg-primary/20 text-primary-foreground text-xs px-1.5 py-0.5 rounded-full">
                {sources.length}
              </span>
            )}
          </SheetTitle>
          <SheetDescription>Manage article sources and references</SheetDescription>
        </SheetHeader>

        <div className="p-3 border-b bg-background flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={onSyncLinksFromArticle}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Sync from Article
          </Button>
          <Button variant="outline" size="sm" onClick={handleAddSource}>
            <Plus className="h-4 w-4 mr-1" />
            Add Source
          </Button>
        </div>

        <ScrollArea className="flex-1 min-h-0">
          <div className="p-4 space-y-3">
            {sources.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                <p className="text-sm">No sources added yet.</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={handleAddSource}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Source
                </Button>
              </div>
            ) : (
              sources.map((source, index) => (
                <div key={index} className="border rounded-lg p-3 space-y-2 bg-background">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Source title"
                        value={source.title}
                        onChange={(e) => handleUpdateSource(index, 'title', e.target.value)}
                        className="h-8 text-sm"
                      />
                      <AiInput
                        placeholder="Description"
                        value={source.description}
                        onChange={(e) => handleUpdateSource(index, 'description', e.target.value)}
                        fieldContext={{
                          fieldName: 'Source Description',
                          pageContext: 'Article Sources',
                          brandId,
                          currentValue: source.description,
                          additionalContext: `Source title: ${source.title}, Link: ${source.link}, Article title: ${articleTitle}`
                        }}
                        onSuggestionAccepted={(suggestion) => handleUpdateSource(index, 'description', suggestion)}
                        className="text-sm"
                      />
                      <Input
                        placeholder="https://..."
                        value={source.link}
                        onChange={(e) => handleUpdateSource(index, 'link', e.target.value)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleRemoveSource(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
